/**
 * useCategoryData — dados da aba "Por Categoria" (Sprint 4).
 *
 * Consome os 2 endpoints novos do backend:
 *   GET /api/nichos-virais/by-category-grouped → categorias + counts (headers)
 *   GET /api/nichos-virais/category-channels   → canais de 1 categoria (paginado)
 *
 * Ambos já aplicam os filtros invisíveis (subs>=100, num_uploads>=3, exclui
 * shorts) e os filtros dinâmicos passados via query params.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

/**
 * Mapeia o modelo de filtros do frontend (useNicheFinderFilters) para os
 * query params dos endpoints de categoria do backend.
 *
 * MISMATCHES CONHECIDOS (confirmados com o Rafael, 2026-05):
 *  - languages[] é multi-select, mas o backend aceita 1 idioma. Aplicamos
 *    SÓ quando EXATAMENTE 1 idioma está selecionado; com 0 ou 2+ não
 *    filtramos idioma nesta aba.
 *  - monetized === false ("só não-monetizados") NÃO é suportado pelo backend
 *    (que só tem monetized_only). É IGNORADO nesta aba.
 *  - addedAfter: janela relativa → added_after (ISO). O backend filtra
 *    last_seen_at — NÃO imported_at (como faz o filtro client-side do grid).
 *  - categories[]: NÃO vai pro backend (que retorna todas). O filtro por
 *    categorias selecionadas é feito client-side no componente.
 */
export function mapFiltersToCategoryParams(filters = {}) {
  const params = {};

  if (filters.search) params.search = filters.search;

  if (Array.isArray(filters.languages) && filters.languages.length === 1) {
    params.language = filters.languages[0];
  }

  if (filters.subsMin != null) params.min_subscribers = filters.subsMin;
  if (filters.subsMax != null) params.max_subscribers = filters.subsMax;
  if (filters.viewsMin != null) params.min_views = filters.viewsMin;
  if (filters.viewsMax != null) params.max_views = filters.viewsMax;
  if (filters.videosMin != null) params.min_videos = filters.videosMin;
  if (filters.videosMax != null) params.max_videos = filters.videosMax;

  if (filters.monetized === true) params.monetized_only = true;

  // Pacote 7.6: filtro Faceless removido do front (toda a base e faceless).
  // O backend ainda aceita faceless_only por retro-compat — apenas nao
  // enviamos mais.

  if (filters.addedAfter && filters.addedAfter !== 'any') {
    const windows = {
      last_hour: 60 * 60 * 1000,
      last_24h: 24 * 60 * 60 * 1000,
      last_7d: 7 * 24 * 60 * 60 * 1000,
      last_30d: 30 * 24 * 60 * 60 * 1000,
    };
    const ms = windows[filters.addedAfter];
    if (ms) params.added_after = new Date(Date.now() - ms).toISOString();
  }

  return params;
}

/** Lista de categorias com counts. Refetch quando os params mudam. */
export function useCategoriesGrouped(categoryParams) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const key = JSON.stringify(categoryParams || {});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API}/nichos-virais/by-category-grouped`,
          { params: categoryParams, headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { categories, loading, error };
}

/** Busca uma página de canais de UMA categoria. Retorna o payload do backend
 *  { category, channels, total, has_more, offset, limit }. */
export async function fetchCategoryChannels(category, offset, limit, categoryParams = {}) {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(
    `${API}/nichos-virais/category-channels`,
    {
      params: { category, offset, limit, ...categoryParams },
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
}
